import Vue from 'vue'
import AuthService from '../api/auth-service'
import AirportService from '../api/airport-service'
import moment from 'moment'

export default {
    namespaced: true,
    state: {
        loggedIn: false,
        checkingSession: false,
        flatCurrentUserPermissions: [],
        appPrefix: {
            inventory: 'inventory',
            admin: 'admin',
            distribution: 'distribution',
            marketing: 'marketing',
            contactCentre: 'contact-centre'
        },
        sessionTimeoutEnabled: true, // enable or disable the entire session timeout functionality
        sessionMoment: null,
        sessionWarning: false,
        sessionCheckRunning: false,
        sessionOptions: {
            timeout: {
                number: 18, // the number to be used for the total timeout duration
                unit: 'minutes', // the unit to be used for the total timeout
                warnBefore: 5 // the number to be used for the timeout warning message
            }
        }

    },
    mutations: {
        setCheckingUserAuthenticated (state, checking) {
            state.checkingSession = checking
        },
        setLoggedIn (state, loggedIn) {
            state.loggedIn = loggedIn
        },
        setFlatCurrentUserPermissions (state, permissions) {
            state.flatCurrentUserPermissions = permissions
        },

        // set the time object against which to check
        setSessionMoment (state, time) {
            state.sessionMoment = time
        },
        // set whether the session warning should show or hide
        setShowSessionWarning (state, showWarning) {
            state.sessionWarning = showWarning
        },
        // set whether the session is being checked or not
        setSessionCheckRunning (state, running) {
            state.sessionCheckRunning = running
        }

    },
    actions: {
        async checkUserAuthenticated (context) {
            context.commit('setCheckingUserAuthenticated', true)
            return AuthService.checkUserAuthenticated().then((response) => {
                if (response.status === 200 && response.data.clientSessionExists) {
                    context.commit('setLoggedIn', true)

                    // reset the session timeout when a user logs in
                    if(context.state.sessionTimeoutEnabled){
                        context.dispatch('resetSessionTimeout')
                    }

                    return response
                }
                context.commit('setLoggedIn', false)

                // set the session check to false
                if(context.state.sessionTimeoutEnabled){
                    context.commit('setSessionCheckRunning', false)
                }

                window.location.href = '/api/auth/login'
                return response
            }).catch(() => {
                context.commit('setLoggedIn', false)

                // set the session check to false
                if(context.state.sessionTimeoutEnabled){
                    context.commit('setSessionCheckRunning', false)
                }

                window.location.href = '/api/auth/login'
            }).finally(() => {
                context.commit('setCheckingUserAuthenticated', false)
            })
        },

        async resetSessionTimeout(context){
            if (!context.state.loggedIn) {
                return
            }
            // Call random endpoint to refresh API
            await AirportService.getAirports()
            .then(() => {
                let timeoutOptions = context.state.sessionOptions.timeout;
                var sessionMoment = moment().add(timeoutOptions.number, timeoutOptions.unit)

                // set the timeout to be 18 minutes upon logging in or resetting
                context.commit('setSessionMoment', sessionMoment)

                // set the showSession warning to false
                context.commit('setShowSessionWarning', false)

                // if there is no sessionCheck currently running, start one
                if (!context.state.sessionCheckRunning) {
                    context.dispatch('startSessionCheck')
                    context.commit('setSessionCheckRunning', true)
                }
                
                // Clear the toast message
                Vue.toasted.clear()
            })
            .catch(async (error) => {
                if (error.response) {
                    if (error.response.status === 401) {
                        window.location.href = '/api/auth/logout'
                    }
                }
            })
        },

        startSessionCheck (context) {
            if (!context.state.loggedIn || !context.state.sessionMoment || context.state.authSessionRunning) {
                return
            } else {
                context.dispatch('checkSession')
            }
        },

        checkSession(context){
            let timeoutOptions = context.state.sessionOptions.timeout;
            if (!context.state.loggedIn || !context.state.sessionMoment) {
                return
            } else {
                let sessionMoment = moment(context.state.sessionMoment);
                // Show a warning toast message if session timer is running low
                if (sessionMoment.diff(moment().add(timeoutOptions.warnBefore, timeoutOptions.unit), 'seconds') < 0) {
                    let timeRemaining = sessionMoment.diff(moment(), 'seconds')
                    if (!context.state.sessionWarning) {
                        this._vueToast = Vue.toasted.show(`You will be logged out in ${moment().startOf('day').seconds(timeRemaining).format('mm:ss')} seconds`, {
                            duration: null,
                            action: [
                                {
                                    text: 'Log Out Now',
                                    onClick: async (e, toastObject) => {
                                        toastObject.goAway(0)
                                        window.location.href = '/api/auth/logout'
                                    },
                                },
                                {
                                    text: 'Keep Me Logged In',
                                    onClick: async (e, toastObject) => {
                                        toastObject.goAway(0)
                                        context.dispatch('resetSessionTimeout')
                                    }
                                }
                            ],
                            type: 'info'

                        })
                        context.commit('setShowSessionWarning', true)
                    }
                    // Create element for the toast msg so it doesn't constantly show and hide new toasts
                    var toastTextElement = document.createElement('span')
                    toastTextElement.innerText = `You will be logged out in ${moment().startOf('day').seconds(timeRemaining).format('mm:ss')}`
                    toastTextElement.className = 'toast-text'
                    this._vueToast.el.appendChild(toastTextElement)
                    this._vueToast.el.replaceChild(toastTextElement, this._vueToast.el.firstChild)
                }
                // When session timer hits 0 - log the user out
                if (sessionMoment.diff(moment(), 'seconds') < 0) {
                    Vue.toasted.show('You have been logged out, please login again to continue', {
                        action: {
                            text: 'Login',
                            push: {
                                name: 'Login'
                            }
                        },
                        type: 'error',
                        duration: null
                    })
                    window.location.href = '/api/auth/logout'
                } else {
                    setTimeout(function () { context.dispatch('checkSession') }, 1000)
                }
            }
        },

        async fetchCurrentPermissions ({
            dispatch,
            commit
        }) {
            return AuthService.getLoggedInUserPermissions()
                .then((response) => {
                    commit('setFlatCurrentUserPermissions', response.data)
                    return response
                })
                .catch((error) => {
                    throw error.response
                })
        },
        async unauthorised ({ commit }) {
            commit('setLoggedIn', false)
        }
    },
    getters: {
        getCurrentUserPermissions (state) {
            var mappedPermissions = { admin: {} }
            state.flatCurrentUserPermissions.forEach(flatPermissionRule => {
                var permissionElements = [...flatPermissionRule.name.matchAll(/[^.]+/g)]
                var inflatedPermissionHierarchy = {}
                permissionElements.reduce(function (o, s, i) {
                    return i === permissionElements.length - 1 ? o[s] = flatPermissionRule : o[s] = {}
                }, inflatedPermissionHierarchy)
                _.merge(mappedPermissions, inflatedPermissionHierarchy)
            })
            return mappedPermissions
        },
        getCurrentUserFlatPermissions (state) {
            return state.flatCurrentUserPermissions
        },
        getContactCentreUserPermissionsLoaded (state, getters) {
            return `${state.appPrefix.contactCentre}` in getters.getCurrentUserPermissions
        },
        userCanEditBookingNotes (state, getters) {
            if (getters.getBookingDetailsUserPermissions['add-note']) return true
            else return false
        },
        userCanEditVehicleBookingDetails (state, getters) {
            if (getters.getBookingDetailsUserPermissions['vehicle-edit']) return true
            else return false
        },
        userCanEditFlightBookingDetails (state, getters) {
            if (getters.getBookingDetailsUserPermissions['flight-edit']) return true
            else return false
        },
        userCanViewBookings (state, getters) {
            if (getters.getBookingDetailsUserPermissions['view']) return true
            else return false
        },
        userCanCreateBookings (state, getters) {
            if (getters.getBookingUserPermissions['create']) return true
            else return false
        },
        userCanCancelBookings (state, getters) {
            if (getters.getBookingDetailsUserPermissions['cancel-booking']) return true
            else return false
        },
        userCanEditBookingProducts (state, getters) {
            if (getters.getBookingDetailsUserPermissions['modify']) return true
            else return false
        },
        userCanRefundBookings (state, getters) {
            if (getters.getBookingDetailsUserPermissions['refund']) return true
            else return false
        },
        userCanEditCustomerDetails (state, getters){
            if (getters.getBookingDetailsUserPermissions['customer-details-edit']) return true
            else return false
        },
        userCanEditBookingTimes (state, getters){
            if (getters.getBookingDetailsUserPermissions['edit-times']) return true
            else return false
        },
        userCanRefundBookingsPreEntry (state, getters) {
            if (getters.getBookingDetailsUserPermissions['partial-refund-pre-entry']) return true
            else return false
        },
        userCanOverridePrice (state, getters) {
            if (getters.getBookingUserPermissions['override-price']) return true
            else return false
        },
        userCanResendConfirmation (state, getters) {
            if (getters.getBookingDetailsUserPermissions['email-resend']) return true
            else return false
        },
        userCanSeeFinancialData (state, getters) {
            if (getters.getSearchUserPermissions['hide-finance']) return false
            else return true
        },
        getBookingDetailsUserPermissions (state, getters) {
            return getContactCentrePermissionsForFeature(state, getters, 'booking-details')
        },
        getBookingUserPermissions (state, getters) {
            return getContactCentrePermissionsForFeature(state, getters, 'booking')
        },
        getSearchUserPermissions (state, getters) {
            return getContactCentrePermissionsForFeature(state, getters, 'search')
        }
    }
}

function getContactCentrePermissionsForFeature (state, getters, featureType) {
    return getters.getContactCentreUserPermissionsLoaded && getters.getCurrentUserPermissions[state.appPrefix.contactCentre][featureType] ? getters.getCurrentUserPermissions[state.appPrefix.contactCentre][featureType] : {}
}